import { handleErrorToast } from 'src/components/molecules';

interface ErrorHandlerCallbacks {
  setLabelError: (error: string) => void;
  setIdentifierError: (error: string) => void;
  setOptionsError: (error: string) => void;
}

interface GraphQLError {
  message: string;
  extensions?: {
    code?: string;
  };
}

interface ErrorWithGraphQL extends Error {
  graphQLErrors?: GraphQLError[];
}

export const handleCustomFieldError = (
  error: Error,
  isEditMode: boolean,
  callbacks: ErrorHandlerCallbacks,
) => {
  const errorMessage = error.message || '';
  const { setLabelError, setIdentifierError, setOptionsError } = callbacks;

  // Extract GraphQL error code if available
  const graphQLError = (error as ErrorWithGraphQL).graphQLErrors?.[0];
  const errorCode = graphQLError?.extensions?.code;

  // Handle CONFLICT errors (duplicate label/identifier)
  if (errorCode === 'CONFLICT') {
    if (errorMessage.toLowerCase().includes('label')) {
      const message =
        'A custom field with this label already exists. Please enter a unique label.';
      setLabelError(message);
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating custom field'
          : 'Error creating custom field',
      });
      return;
    }
    if (errorMessage.toLowerCase().includes('identifier')) {
      const message =
        'A custom field with this identifier already exists. Please enter a unique identifier.';
      setIdentifierError(message);
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating custom field'
          : 'Error creating custom field',
      });
      return;
    }
  }

  // Handle BAD_USER_INPUT errors (validation failures)
  if (errorCode === 'BAD_USER_INPUT') {
    if (errorMessage.toLowerCase().includes('must have at least one option')) {
      setOptionsError('Single select fields must have at least one option');
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating custom field'
          : 'Error creating custom field',
      });
      return;
    }
    if (errorMessage.toLowerCase().includes('option labels must be unique')) {
      setOptionsError('Option labels must be unique');
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating custom field'
          : 'Error creating custom field',
      });
      return;
    }
    // Display the error message inline for other BAD_USER_INPUT errors
    if (errorMessage.toLowerCase().includes('label')) {
      setLabelError(errorMessage);
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating custom field'
          : 'Error creating custom field',
      });
      return;
    }
    if (errorMessage.toLowerCase().includes('identifier')) {
      setIdentifierError(errorMessage);
      handleErrorToast({
        toastMessage: isEditMode
          ? 'Error updating custom field'
          : 'Error creating custom field',
      });
      return;
    }
  }

  // Fallback for legacy string matching (if GraphQL error code not available)
  if (errorMessage.toLowerCase().includes('label already exists')) {
    const message =
      'A custom field with this label already exists. Please enter a unique label.';
    setLabelError(message);
    handleErrorToast({
      toastMessage: isEditMode
        ? 'Error updating custom field'
        : 'Error creating custom field',
    });
  } else if (errorMessage.toLowerCase().includes('identifier already exists')) {
    const message =
      'A custom field with this identifier already exists. Please enter a unique identifier.';
    setIdentifierError(message);
    handleErrorToast({
      toastMessage: isEditMode
        ? 'Error updating custom field'
        : 'Error creating custom field',
    });
  } else if (
    errorMessage.toLowerCase().includes('must have at least one option')
  ) {
    setOptionsError('Single select fields must have at least one option');
    handleErrorToast({
      toastMessage: isEditMode
        ? 'Error updating custom field'
        : 'Error creating custom field',
    });
  } else if (
    errorMessage.toLowerCase().includes('option labels must be unique')
  ) {
    setOptionsError('Option labels must be unique');
    handleErrorToast({
      toastMessage: isEditMode
        ? 'Error updating custom field'
        : 'Error creating custom field',
    });
  } else {
    handleErrorToast({
      toastMessage: isEditMode
        ? 'Error updating custom field'
        : 'Error creating custom field',
    });
  }
};
